import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  createProjectCommunicationDraft,
  createProjectPerson,
  createProjectRoleNeed,
  getGateWorkflow,
  getProjectOperations,
  updateProjectFollowUp,
  updateProjectRoleNeed,
  type CommunicationDraft,
  type Gate,
  type GateDeliverable,
  type GateWorkflow,
  type ProjectFollowUp,
  type ProjectOperations,
  type ProjectPerson,
  type ProjectFollowUpStatus,
  type ProjectRoleNeed,
  type RequirementSourceRef,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { recordV2ErrorStage, recordV2FunnelStep } from '../../utils/v2Rum';
import {
  AssignRolePersonModal,
  TextEntryModal,
  type AssignRolePersonValues,
} from './SelaProjectOperationModals';
import { useAskSela } from './ask-sela/AskSelaContext';

type WorkspaceContext = {
  project: SelaProjectDetail;
};

const EMPTY_WORKFLOW: GateWorkflow = {
  workflowStatus: 'pending_decision_brief',
  gates: [],
  stageProfiles: [],
  deliverables: [],
  approvals: [],
  blockers: [],
  dependencies: [],
  timelineEvents: [],
};

const EMPTY_OPERATIONS: ProjectOperations = {
  people: [],
  roleNeeds: [],
  suggestedRoleNeeds: [],
  followUps: [],
  drafts: [],
  summary: {
    peopleCount: 0,
    openFollowUpCount: 0,
    overdueFollowUpCount: 0,
    waitingOnDocumentCount: 0,
    unassignedRoleNeedCount: 0,
  },
};

const PERSON_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  waiting_on_response: 'Waiting on response',
  waiting_on_document: 'Waiting on document',
  overdue_follow_up: 'Overdue action',
  no_open_asks: 'No open actions',
  archived: 'Archived',
};

const FOLLOW_UP_STATUS_LABEL: Record<string, string> = {
  draft_needed: 'Draft needed',
  draft_ready: 'Draft ready',
  sent: 'Sent',
  waiting_on_response: 'Waiting on response',
  waiting_on_document: 'Waiting on document',
  needs_user_review: 'Needs review',
  completed: 'Completed',
  closed: 'Closed',
  blocked: 'Blocked',
};

const ACTION_STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  waiting: 'Waiting on someone',
  waiting_on_someone: 'Waiting on someone',
  waiting_on_document: 'Waiting on document',
  needs_review: 'Needs review',
  blocked: 'Blocked',
  complete: 'Complete',
  skipped: 'Skipped / accepted risk',
  not_applicable: 'Not applicable',
};

const STATUS_TONE: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700',
  waiting_on_response: 'bg-amber-50 text-amber-700',
  waiting_on_document: 'bg-orange-50 text-orange-700',
  overdue_follow_up: 'bg-rose-50 text-rose-700',
  no_open_asks: 'bg-slate-100 text-slate-600',
  archived: 'bg-slate-100 text-slate-500',
  suggested: 'bg-indigo-50 text-indigo-700',
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-50 text-blue-700',
  waiting: 'bg-amber-50 text-amber-700',
  waiting_on_someone: 'bg-amber-50 text-amber-700',
  draft_needed: 'bg-slate-100 text-slate-700',
  draft_ready: 'bg-blue-50 text-blue-700',
  sent: 'bg-indigo-50 text-indigo-700',
  needs_review: 'bg-amber-50 text-amber-700',
  needs_user_review: 'bg-purple-50 text-purple-700',
  complete: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-emerald-50 text-emerald-700',
  skipped: 'bg-slate-100 text-slate-600',
  not_applicable: 'bg-slate-100 text-slate-600',
  closed: 'bg-slate-100 text-slate-600',
  blocked: 'bg-rose-50 text-rose-700',
};

const OPEN_STATUSES = new Set([
  'draft_needed',
  'draft_ready',
  'sent',
  'waiting_on_response',
  'waiting_on_document',
  'needs_user_review',
  'blocked',
]);

function shortDate(value?: string | null): string {
  if (!value) return 'None';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return value;
  return new Date(timestamp).toLocaleDateString();
}

function isOverdue(value?: string): boolean {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;
  return timestamp < Date.now();
}

function statusPill(status?: string) {
  const key = status || 'active';
  const label = PERSON_STATUS_LABEL[key] || FOLLOW_UP_STATUS_LABEL[key] || key.replace(/_/g, ' ');
  return (
    <span className={['rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_TONE[key] || 'bg-slate-100 text-slate-700'].join(' ')}>
      {label}
    </span>
  );
}

function MoreMenu({ children }: { children: (close: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="rounded-md border border-[#cdd5e1] bg-white px-2.5 py-1 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
      >
        More
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] rounded-lg border border-[#d8e0eb] bg-white p-1 text-xs shadow-lg" role="menu">
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}

function refsLabel(refs: RequirementSourceRef[]): string {
  if (refs.length === 0) return 'No roadmap actions yet';
  return refs.map((ref) => ref.label || ref.sourceId).join(', ');
}

function followUpsForPerson(followUps: ProjectFollowUp[], personId: string): ProjectFollowUp[] {
  return followUps.filter((item) => item.personId === personId);
}

function followUpsForRoleNeed(followUps: ProjectFollowUp[], roleNeedId: string): ProjectFollowUp[] {
  return followUps.filter((item) => item.roleNeedId === roleNeedId);
}

function personName(people: ProjectPerson[], followUp: ProjectFollowUp): string {
  const person = people.find((item) => item.personId === followUp.personId);
  return person?.name || followUp.responsibleRole || 'Unassigned role';
}

function primaryRef(followUp: ProjectFollowUp): RequirementSourceRef | undefined {
  return followUp.requirementRef;
}

function refActionKey(ref?: RequirementSourceRef): string {
  if (!ref) return '';
  return ref.deliverableId || ref.requirementId || ref.sourceId || '';
}

function actionKey(action?: GateDeliverable): string {
  return action?.deliverableId || action?.requirementId || '';
}

function actionStatusLabel(status?: string): string {
  return ACTION_STATUS_LABEL[status || ''] || (status || '').replace(/_/g, ' ') || 'Not started';
}

function stageLabelForAction(action: GateDeliverable | undefined, gatesById: Map<string, Gate>): string {
  if (!action) return 'Roadmap';
  const gate = gatesById.get(action.gateId);
  return gate?.label || action.gateId || 'Roadmap';
}

export function SelaProjectPeopleFollowUpsPage() {
  const { project } = useOutletContext<WorkspaceContext>();
  const projectId = project.id;
  const [operations, setOperations] = useState<ProjectOperations>(EMPTY_OPERATIONS);
  const [workflow, setWorkflow] = useState<GateWorkflow>(EMPTY_WORKFLOW);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [roleNeedToAssign, setRoleNeedToAssign] = useState<ProjectRoleNeed | null>(null);
  const [noteTarget, setNoteTarget] = useState<ProjectFollowUp | null>(null);
  const [selectedAction, setSelectedAction] = useState<ProjectFollowUp | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<CommunicationDraft | null>(null);
  const [statusTarget, setStatusTarget] = useState<ProjectFollowUp | null>(null);
  const [draftingFollowUpId, setDraftingFollowUpId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [nextOperations, nextWorkflow] = await Promise.all([
        getProjectOperations(projectId),
        getGateWorkflow(projectId),
      ]);
      setOperations(nextOperations);
      setWorkflow(nextWorkflow);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roster.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const allRoleNeeds = useMemo(() => {
    const persistedIds = new Set(operations.roleNeeds.map((item) => item.roleNeedId));
    return [
      ...operations.roleNeeds,
      ...operations.suggestedRoleNeeds.filter((item) => !persistedIds.has(item.roleNeedId)),
    ];
  }, [operations.roleNeeds, operations.suggestedRoleNeeds]);
  const requiredRoleOptions = useMemo(
    () => Array.from(new Set(allRoleNeeds.map((item) => item.role).filter(Boolean))).sort(),
    [allRoleNeeds],
  );

  const unassignedRoleNeeds = allRoleNeeds.filter(
    (item) => !item.assignedPersonId && !['not_needed', 'archived'].includes(item.status),
  );

  const actionsById = useMemo(() => {
    const map = new Map<string, GateDeliverable>();
    for (const action of workflow.deliverables) {
      map.set(actionKey(action), action);
    }
    return map;
  }, [workflow.deliverables]);
  const gatesById = useMemo(() => {
    const map = new Map<string, Gate>();
    for (const gate of workflow.gates) {
      map.set(gate.gateId, gate);
    }
    return map;
  }, [workflow.gates]);

  const createPerson = async (values: AssignRolePersonValues) => {
    setBusy(true);
    recordV2FunnelStep('roster_add_person', 'start', { source_page: 'roster' }, projectId);
    try {
      await createProjectPerson(projectId, {
        name: values.name,
        roles: [values.role],
        company: values.company,
        email: values.email,
      });
      setShowAddPersonModal(false);
      await reload();
      recordV2FunnelStep('roster_add_person', 'success', { source_page: 'roster' }, projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add roster member.');
      recordV2FunnelStep('roster_add_person', 'failure', { source_page: 'roster' }, projectId);
      recordV2ErrorStage('roster_add_person', err, {}, projectId);
    } finally {
      setBusy(false);
    }
  };

  const addPersonForRoleNeed = async (roleNeed: ProjectRoleNeed, values: AssignRolePersonValues) => {
    setBusy(true);
    try {
      const person = await createProjectPerson(projectId, {
        name: values.name,
        roles: [values.role || roleNeed.role],
        company: values.company,
        email: values.email,
        linkedRequirementRefs: roleNeed.linkedRequirementRefs,
        relationshipContext: roleNeed.whyNeeded,
      });
      if (!roleNeed.derived) {
        await updateProjectRoleNeed(projectId, roleNeed.roleNeedId, {
          assignedPersonId: person.personId,
          status: 'assigned',
        });
      }
      setRoleNeedToAssign(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign role.');
    } finally {
      setBusy(false);
    }
  };

  const markRoleNeedNotNeeded = async (roleNeed: ProjectRoleNeed) => {
    setBusy(true);
    try {
      if (roleNeed.derived) {
        await createProjectRoleNeed(projectId, {
          role: roleNeed.role,
          status: 'not_needed',
          whyNeeded: roleNeed.whyNeeded,
          suggestedQuestions: roleNeed.suggestedQuestions,
          linkedRequirementRefs: roleNeed.linkedRequirementRefs,
        });
      } else {
        await updateProjectRoleNeed(projectId, roleNeed.roleNeedId, { status: 'not_needed' });
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role need.');
    } finally {
      setBusy(false);
    }
  };

  const updateFollowUpStatus = async (followUp: ProjectFollowUp, status: ProjectFollowUp['status']) => {
    setBusy(true);
    recordV2FunnelStep('roster_followup_status_update', 'start', {
      follow_up_id: followUp.followUpId,
      status,
      source_page: 'roster',
    }, projectId);
    try {
      await updateProjectFollowUp(projectId, followUp.followUpId, { status });
      await reload();
      recordV2FunnelStep('roster_followup_status_update', 'success', {
        follow_up_id: followUp.followUpId,
        status,
        source_page: 'roster',
      }, projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update action.');
      recordV2FunnelStep('roster_followup_status_update', 'failure', { source_page: 'roster' }, projectId);
      recordV2ErrorStage('roster_followup_status_update', err, {}, projectId);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (followUp: ProjectFollowUp, note: string) => {
    setBusy(true);
    try {
      await updateProjectFollowUp(projectId, followUp.followUpId, { notes: note });
      setNoteTarget(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add note.');
    } finally {
      setBusy(false);
    }
  };

  const generateDraft = async (followUp: ProjectFollowUp) => {
    setBusy(true);
    setDraftingFollowUpId(followUp.followUpId);
    recordV2FunnelStep('roster_followup_draft', 'start', {
      follow_up_id: followUp.followUpId,
      source_page: 'roster',
    }, projectId);
    try {
      const result = await createProjectCommunicationDraft(projectId, followUp.followUpId);
      setSelectedDraft(result.draft);
      setSelectedAction(result.followUp);
      await reload();
      recordV2FunnelStep('roster_followup_draft', 'success', {
        follow_up_id: followUp.followUpId,
        source_page: 'roster',
      }, projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to draft email.');
      recordV2FunnelStep('roster_followup_draft', 'failure', { source_page: 'roster' }, projectId);
      recordV2ErrorStage('roster_followup_draft', err, {}, projectId);
    } finally {
      setBusy(false);
      setDraftingFollowUpId(null);
    }
  };

  const copyDraft = async (draft: CommunicationDraft) => {
    await navigator.clipboard.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    setNotice('Draft copied.');
  };

  return (
    <main className="flex-1 overflow-auto p-6 md:p-8">
      <div className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">Roster</h1>
          <p className="mt-2 max-w-3xl text-sm text-[#647488]">
            See who is involved in the project and which roadmap actions they are responsible for.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAddPersonModal(true)}
            className="rounded-md bg-[#3251b7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#28449f]"
          >
            Add roster member
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}
      {notice ? (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div>
      ) : null}

      {loading ? (
        <div className="rounded-xl border border-[#e1e8f0] bg-white p-6 text-sm text-[#647488] shadow-sm">Loading roster...</div>
      ) : (
        <PeopleView
          people={operations.people}
          roleNeeds={unassignedRoleNeeds}
          followUps={operations.followUps}
          actionsById={actionsById}
          gatesById={gatesById}
          onAddPersonForRoleNeed={setRoleNeedToAssign}
          onMarkRoleNeedNotNeeded={(roleNeed) => void markRoleNeedNotNeeded(roleNeed)}
          onAddNote={setNoteTarget}
          onSelectAction={setSelectedAction}
          onDraft={(followUp) => void generateDraft(followUp)}
          draftingFollowUpId={draftingFollowUpId}
        />
      )}

      {roleNeedToAssign ? (
        <AssignRolePersonModal
          role={roleNeedToAssign.role}
          roleOptions={requiredRoleOptions}
          busy={busy}
          onClose={() => setRoleNeedToAssign(null)}
          onSubmit={(values) => void addPersonForRoleNeed(roleNeedToAssign, values)}
        />
      ) : null}

      {showAddPersonModal ? (
        <AssignRolePersonModal
          role={requiredRoleOptions[0] || 'Architect / designer'}
          roleOptions={requiredRoleOptions}
          title="Add roster member"
          description="Add someone involved in this project, then assign roadmap actions as diligence work comes up."
          busy={busy}
          onClose={() => setShowAddPersonModal(false)}
          onSubmit={(values) => void createPerson(values)}
        />
      ) : null}

      {noteTarget ? (
        <TextEntryModal
          title="Add response note"
          description="Record what changed after this roster member responded. This does not automatically complete the roadmap action."
          defaultValue={noteTarget.notes || noteTarget.outcome || ''}
          submitLabel="Save note"
          busy={busy}
          onClose={() => setNoteTarget(null)}
          onSubmit={(value) => void addNote(noteTarget, value)}
        />
      ) : null}

      {selectedAction ? (
        <ActionDetailsModal
          action={selectedAction}
          draft={selectedDraft?.followUpId === selectedAction.followUpId ? selectedDraft : null}
          rosterMemberName={personName(operations.people, selectedAction)}
          onClose={() => {
            setSelectedAction(null);
            setSelectedDraft(null);
          }}
          onDraft={(followUp) => void generateDraft(followUp)}
          onCopyDraft={(draft) => void copyDraft(draft)}
          onAddNote={setNoteTarget}
          onUpdateStatus={setStatusTarget}
          isDrafting={draftingFollowUpId === selectedAction.followUpId}
        />
      ) : null}

      {statusTarget ? (
        <UpdateActionStatusModal
          action={statusTarget}
          busy={busy}
          onClose={() => setStatusTarget(null)}
          onSubmit={(status) => {
            void updateFollowUpStatus(statusTarget, status);
            setStatusTarget(null);
          }}
        />
      ) : null}

    </main>
  );
}

function PeopleView({
  people,
  roleNeeds,
  followUps,
  actionsById,
  gatesById,
  onAddPersonForRoleNeed,
  onMarkRoleNeedNotNeeded,
  onAddNote,
  onSelectAction,
  onDraft,
  draftingFollowUpId,
}: {
  people: ProjectPerson[];
  roleNeeds: ProjectRoleNeed[];
  followUps: ProjectFollowUp[];
  actionsById: Map<string, GateDeliverable>;
  gatesById: Map<string, Gate>;
  onAddPersonForRoleNeed: (roleNeed: ProjectRoleNeed) => void;
  onMarkRoleNeedNotNeeded: (roleNeed: ProjectRoleNeed) => void;
  onAddNote: (followUp: ProjectFollowUp) => void;
  onSelectAction: (followUp: ProjectFollowUp) => void;
  onDraft: (followUp: ProjectFollowUp) => void;
  draftingFollowUpId?: string | null;
}) {
  const [showNeededRoles, setShowNeededRoles] = useState(false);

  if (people.length === 0 && roleNeeds.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-[#f8fafc] p-8 text-sm text-[#647488]">
        No roster members or role needs yet. Add a roster member, or use suggested role slots from unresolved roadmap actions as they appear.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#334155]">Roster members</h2>
            <p className="mt-1 text-xs text-[#647488]">
              Named roster members involved in this parcel evaluation.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-[#475669]">
            {people.length}
          </span>
        </div>
        {people.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-white p-6 text-sm text-[#647488]">
            No roster members have been added yet. Add one from the header or assign one from a needed role.
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {people.map((person) => (
              <PersonCard
                key={person.personId}
                person={person}
                followUps={followUpsForPerson(followUps, person.personId)}
                actionsById={actionsById}
                gatesById={gatesById}
                onSelectAction={onSelectAction}
                onDraft={onDraft}
                onAddNote={onAddNote}
                draftingFollowUpId={draftingFollowUpId}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-end justify-between gap-3 border-t border-[#e1e8f0] pt-6">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#334155]">Needed roles</h2>
            <p className="mt-1 text-xs text-[#647488]">
              Role slots Sela thinks may be needed before the project has an assigned roster member.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
              {roleNeeds.length}
            </span>
            <button
              type="button"
              onClick={() => setShowNeededRoles((current) => !current)}
              aria-expanded={showNeededRoles}
              className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
            >
              {showNeededRoles ? 'Hide roles' : 'Show roles'}
            </button>
          </div>
        </div>
        {!showNeededRoles ? null : roleNeeds.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#cdd5e1] bg-[#f8fafc] p-6 text-sm text-[#647488]">
            No unassigned needed roles are currently surfaced.
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {roleNeeds.map((roleNeed) => (
              <RoleNeedCard
                key={roleNeed.roleNeedId}
                roleNeed={roleNeed}
                followUps={followUpsForRoleNeed(followUps, roleNeed.roleNeedId)}
                actionsById={actionsById}
                onAddPerson={onAddPersonForRoleNeed}
                onMarkNotNeeded={onMarkRoleNeedNotNeeded}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PersonCard({
  person,
  followUps,
  actionsById,
  gatesById,
  onSelectAction,
  onDraft,
  onAddNote,
  draftingFollowUpId,
}: {
  person: ProjectPerson;
  followUps: ProjectFollowUp[];
  actionsById: Map<string, GateDeliverable>;
  gatesById: Map<string, Gate>;
  onSelectAction: (followUp: ProjectFollowUp) => void;
  onDraft: (followUp: ProjectFollowUp) => void;
  onAddNote: (followUp: ProjectFollowUp) => void;
  draftingFollowUpId?: string | null;
}) {
  const { openAskSela } = useAskSela();
  const openActions = followUps.filter((item) => OPEN_STATUSES.has(item.status));
  const overdueActions = openActions.filter((item) => isOverdue(item.dueDate));
  const waitingOnDocuments = openActions.filter((item) => item.status === 'waiting_on_document' || item.requestedDocument);
  return (
    <article className="rounded-xl border border-[#e1e8f0] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#0f172a]">{person.name}</h2>
          <p className="mt-1 text-sm text-[#647488]">
            {(person.roles || []).join(', ') || 'Roster member'}{person.company ? ` - ${person.company}` : ''}
          </p>
          <p className="mt-1 text-xs text-[#647488]">
            {[person.email, person.phone].filter(Boolean).join(' · ') || 'No contact info yet'}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {statusPill(person.derivedStatus || person.status)}
          <MoreMenu>
            {(close) => (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  openAskSela({
                    type: 'person',
                    label: person.name,
                    sourceId: person.personId,
                    sourcePage: 'people',
                    summary: person.relationshipContext || (person.roles || []).join(', '),
                    metadata: {
                      roles: person.roles,
                      company: person.company,
                      status: person.derivedStatus || person.status,
                      openActionCount: openActions.length,
                      overdueActionCount: overdueActions.length,
                      waitingOnDocumentCount: waitingOnDocuments.length,
                      nextFollowUpAt: person.nextFollowUpAt,
                      assignedActions: followUps.map((item) => ({
                        followUpId: item.followUpId,
                        title: item.title,
                        openAsk: item.openAsk,
                        status: item.status,
                        dueDate: item.dueDate,
                        requirementRef: item.requirementRef,
                      })),
                    },
                  }, `What are we waiting on from ${person.name}?`);
                  close();
                }}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
              >
                Ask Sela
              </button>
            )}
          </MoreMenu>
        </div>
      </div>
      <div className="mt-4 grid gap-2 text-xs text-[#475669] sm:grid-cols-4">
        <div className="rounded-lg bg-[#f8fafc] px-3 py-2">Open actions: <b>{openActions.length}</b></div>
        <div className="rounded-lg bg-[#f8fafc] px-3 py-2">Overdue: <b>{overdueActions.length}</b></div>
        <div className="rounded-lg bg-[#f8fafc] px-3 py-2">Waiting docs: <b>{waitingOnDocuments.length}</b></div>
        <div className="rounded-lg bg-[#f8fafc] px-3 py-2">Next follow-up: <b>{shortDate(person.nextFollowUpAt)}</b></div>
      </div>
      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Assigned actions</h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-[#647488]">
            {followUps.length}
          </span>
        </div>
        {followUps.length > 0 ? (
          <div className="space-y-2">
            {followUps.map((followUp) => (
              <ActionListRow
                key={followUp.followUpId}
                followUp={followUp}
                roadmapAction={actionsById.get(refActionKey(primaryRef(followUp)))}
                stageLabel={stageLabelForAction(actionsById.get(refActionKey(primaryRef(followUp))), gatesById)}
                onClick={() => onSelectAction(followUp)}
                onDraft={() => onDraft(followUp)}
                onAddNote={() => onAddNote(followUp)}
                isDrafting={draftingFollowUpId === followUp.followUpId}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-[#f8fafc] px-3 py-3 text-xs text-[#647488]">
            No assigned actions yet. Assign an action from the Roadmap.
          </div>
        )}
      </div>
    </article>
  );
}

function RoleNeedCard({
  roleNeed,
  followUps,
  actionsById,
  onAddPerson,
  onMarkNotNeeded,
}: {
  roleNeed: ProjectRoleNeed;
  followUps: ProjectFollowUp[];
  actionsById: Map<string, GateDeliverable>;
  onAddPerson: (roleNeed: ProjectRoleNeed) => void;
  onMarkNotNeeded: (roleNeed: ProjectRoleNeed) => void;
}) {
  const { openAskSela } = useAskSela();
  const [showDetails, setShowDetails] = useState(false);
  const actionCount = roleNeed.linkedRequirementRefs?.length || 0;
  return (
    <article className="rounded-xl border border-dashed border-[#b9c7dc] bg-[#f8fbff] p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Needed role</div>
          <h2 className="mt-1 text-base font-bold text-[#0f172a]">{roleNeed.role}</h2>
          <p className="mt-2 text-sm text-[#647488]">
            Needed for {actionCount} roadmap action{actionCount === 1 ? '' : 's'}.
          </p>
        </div>
        {statusPill(roleNeed.status || 'suggested')}
      </div>
      {showDetails ? (
        <div className="mt-4 rounded-lg border border-[#e1e8f0] bg-white px-3 py-3">
          <p className="text-sm text-[#475669]">{roleNeed.whyNeeded || 'Needed for unresolved roadmap work.'}</p>
          <div className="mt-3 text-xs text-[#647488]">
            <span className="font-semibold text-[#334155]">Needed for actions: </span>
            {refsLabel(roleNeed.linkedRequirementRefs || [])}
          </div>
          {roleNeed.linkedRequirementRefs?.length ? (
            <ul className="mt-3 space-y-1 text-xs text-[#475669]">
              {roleNeed.linkedRequirementRefs.slice(0, 4).map((ref) => {
                const action = actionsById.get(refActionKey(ref));
                return (
                  <li key={`${ref.sourceType}-${refActionKey(ref)}`} className="rounded-lg bg-[#f8fafc] px-3 py-2">
                    {ref.label || action?.label || ref.sourceId}
                    <span className="ml-2 text-[#647488]">{action ? actionStatusLabel(action.status) : 'Unassigned role slot'}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {roleNeed.suggestedQuestions?.length ? (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-[#475669]">
              {roleNeed.suggestedQuestions.slice(0, 3).map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          ) : null}
          {followUps.length > 0 ? (
            <div className="mt-3 rounded-lg bg-[#f8fafc] px-3 py-2 text-xs text-[#475669]">
              {followUps.length} assigned action{followUps.length === 1 ? '' : 's'}.
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => onAddPerson(roleNeed)} className="rounded-md bg-[#3251b7] px-3 py-1.5 text-xs font-semibold text-white">Add roster member</button>
        <button type="button" onClick={() => setShowDetails((current) => !current)} className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474]">
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
        <MoreMenu>
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onMarkNotNeeded(roleNeed);
                  close();
                }}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#647488] hover:bg-[#f8fafc]"
              >
                Mark not needed
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  openAskSela({
                    type: 'roleNeed',
                    label: roleNeed.role,
                    sourceId: roleNeed.roleNeedId,
                    sourcePage: 'people',
                    summary: roleNeed.whyNeeded,
                    metadata: {
                      status: roleNeed.status,
                      linkedRequirementRefs: roleNeed.linkedRequirementRefs,
                      linkedActions: (roleNeed.linkedRequirementRefs || []).map((ref) => {
                        const action = actionsById.get(refActionKey(ref));
                        return {
                          ref,
                          label: ref.label || action?.label,
                          roadmapStatus: action?.status,
                          gateId: action?.gateId,
                          ownerRole: action?.ownerRole,
                        };
                      }),
                      suggestedQuestions: roleNeed.suggestedQuestions,
                    },
                  }, `What should I ask a ${roleNeed.role}?`);
                  close();
                }}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
              >
                Ask Sela
              </button>
            </>
          )}
        </MoreMenu>
      </div>
    </article>
  );
}

function ActionListRow({
  followUp,
  roadmapAction,
  stageLabel,
  onClick,
  onDraft,
  onAddNote,
  isDrafting,
}: {
  followUp: ProjectFollowUp;
  roadmapAction?: GateDeliverable;
  stageLabel: string;
  onClick: () => void;
  onDraft: () => void;
  onAddNote: () => void;
  isDrafting?: boolean;
}) {
  const { openAskSela } = useAskSela();
  return (
    <div className="rounded-lg border border-[#e1e8f0] bg-[#f8fafc] px-3 py-3 transition hover:border-[#3251b7] hover:bg-white">
      <div className="flex items-start justify-between gap-3">
        <div>
          <button type="button" onClick={onClick} className="text-left text-sm font-semibold text-[#0f172a] hover:text-[#3251b7] hover:underline">{primaryRef(followUp)?.label || followUp.openAsk}</button>
          <div className="mt-1 text-xs text-[#647488]">
            {stageLabel} · {actionStatusLabel(roadmapAction?.status)} · Follow-up {FOLLOW_UP_STATUS_LABEL[followUp.status] || followUp.status} · Due {shortDate(followUp.dueDate)}
          </div>
        </div>
        {statusPill(roadmapAction?.status || followUp.status)}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={isDrafting}
          onClick={onDraft}
          className="rounded-md bg-[#3251b7] px-2.5 py-1 text-xs font-semibold text-white disabled:cursor-wait disabled:opacity-60"
        >
          {isDrafting ? 'Drafting email...' : 'Draft follow-up'}
        </button>
        <MoreMenu>
          {(close) => (
            <>
              <Link
                to="../timeline"
                role="menuitem"
                onClick={close}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
              >
                View action
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onAddNote();
                  close();
                }}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
              >
                Add note
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  openAskSela({
                    type: 'followUp',
                    label: followUp.openAsk,
                    sourceId: followUp.followUpId,
                    sourcePage: 'people',
                    summary: followUp.communicationSummary || followUp.notes || '',
                    metadata: {
                      status: followUp.status,
                      dueDate: followUp.dueDate,
                      requirementRef: followUp.requirementRef,
                      roadmapActionId: roadmapAction?.deliverableId,
                      roadmapActionLabel: roadmapAction?.label,
                      roadmapActionStatus: roadmapAction?.status,
                      roadmapActionImportance: roadmapAction?.importance,
                      roadmapActionOwnerRole: roadmapAction?.ownerRole,
                      roadmapActionHowToResolve: roadmapAction?.howToResolve,
                      stageLabel,
                      requestedDocument: followUp.requestedDocument,
                      questions: followUp.questions,
                    },
                  }, 'Draft a follow-up email');
                  close();
                }}
                className="block w-full rounded-md px-3 py-2 text-left font-semibold text-[#1e4474] hover:bg-[#f8fafc]"
              >
                Ask Sela
              </button>
            </>
          )}
        </MoreMenu>
      </div>
    </div>
  );
}

function ActionDetailsModal({
  action,
  draft,
  rosterMemberName,
  onClose,
  onDraft,
  onCopyDraft,
  onAddNote,
  onUpdateStatus,
  isDrafting,
}: {
  action: ProjectFollowUp;
  draft: CommunicationDraft | null;
  rosterMemberName: string;
  onClose: () => void;
  onDraft: (followUp: ProjectFollowUp) => void;
  onCopyDraft: (draft: CommunicationDraft) => void;
  onAddNote: (followUp: ProjectFollowUp) => void;
  onUpdateStatus: (followUp: ProjectFollowUp) => void;
  isDrafting?: boolean;
}) {
  const requirement = primaryRef(action);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-2xl rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Action details</p>
            <h2 className="mt-1 text-lg font-bold text-[#0f172a]">{action.openAsk}</h2>
            <p className="mt-1 text-sm text-[#647488]">{rosterMemberName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="mt-5 grid gap-3 text-sm md:grid-cols-2">
          <DetailItem label="Status">{statusPill(action.status)}</DetailItem>
          <DetailItem label="Due date">{shortDate(action.dueDate)}</DetailItem>
          <DetailItem label="Roadmap action">{requirement?.label || 'No roadmap action'}</DetailItem>
          <DetailItem label="Requested document">{action.requestedDocument || 'None'}</DetailItem>
          <DetailItem label="Last outreach">{shortDate(action.lastContactedAt)}</DetailItem>
          <DetailItem label="Related risk">{action.relatedRisk || action.relatedMissingFact || 'None'}</DetailItem>
        </div>

        {action.questions?.length ? (
          <div className="mt-5 rounded-lg bg-[#f8fafc] p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Questions</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[#334155]">
              {action.questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-5 rounded-lg bg-[#f8fafc] p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Notes / outcome</div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-[#334155]">
            {action.notes || action.outcome || action.communicationSummary || 'No notes recorded yet.'}
          </p>
        </div>

        {draft ? (
          <div className="mt-5 rounded-lg border border-[#d8e0eb] bg-white p-4">
            <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-[#647488]">Draft email</div>
                <p className="mt-1 text-sm font-semibold text-[#1e4474]">{draft.subject}</p>
              </div>
              <button type="button" onClick={() => onCopyDraft(draft)} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white">
                Copy draft
              </button>
            </div>
            <pre className="mt-4 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-[#f8fafc] p-4 text-xs leading-5 text-[#334155]">
              {draft.body}
            </pre>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            disabled={isDrafting}
            onClick={() => onDraft(action)}
            className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:cursor-wait disabled:opacity-60"
          >
            {isDrafting ? 'Drafting email...' : 'Draft email'}
          </button>
          <button type="button" onClick={() => onAddNote(action)} className="rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-xs font-semibold text-[#1e4474]">
            Add note
          </button>
          <button type="button" onClick={() => onUpdateStatus(action)} className="rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-xs font-semibold text-[#1e4474]">
            Update status
          </button>
        </div>
      </div>
    </div>
  );
}

const ACTION_STATUS_OPTIONS: Array<{ value: ProjectFollowUpStatus; label: string }> = [
  { value: 'draft_needed', label: 'Draft needed' },
  { value: 'draft_ready', label: 'Draft ready' },
  { value: 'sent', label: 'Sent' },
  { value: 'waiting_on_response', label: 'Waiting on response' },
  { value: 'waiting_on_document', label: 'Waiting on document' },
  { value: 'needs_user_review', label: 'Needs user review' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'blocked', label: 'Blocked' },
];

function UpdateActionStatusModal({
  action,
  busy,
  onClose,
  onSubmit,
}: {
  action: ProjectFollowUp;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (status: ProjectFollowUpStatus) => void;
}) {
  const [status, setStatus] = useState<ProjectFollowUpStatus>(
    ACTION_STATUS_OPTIONS.some((option) => option.value === action.status)
      ? (action.status as ProjectFollowUpStatus)
      : 'draft_needed',
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-md rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-[#0f172a]">Update status</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#647488]">{action.openAsk}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-[#647488]">
          Status
        </label>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as ProjectFollowUpStatus)}
          className="mt-2 w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm text-[#0f172a]"
        >
          {ACTION_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="button" disabled={busy} onClick={() => onSubmit(status)} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Save status
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-[#f8fafc] px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#647488]">{label}</div>
      <div className="mt-1 text-[#334155]">{children}</div>
    </div>
  );
}
